import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Flex,
  Checkbox,
  Tag,
  Text,
  HStack,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react'
import { LuRocket } from 'react-icons/lu'
import { useFormContext } from 'react-hook-form'
import { RequestFormData } from '..'
import { useState, useEffect } from 'react'
import { ITag } from '../../../../types/tags'
import { useGetTags } from '../../../../api/api_hooks/tags'
import FilePreview from '../../../FilePreview'
import { useTranslation } from 'react-i18next'

export default function ReviewStep() {
  const { watch, register } = useFormContext<RequestFormData>()
  const formData = watch()
  const [selectedTags, setSelectedTags] = useState<ITag[]>([])
  const { t } = useTranslation()

  const { data: allTags } = useGetTags()

  useEffect(() => {
    if (allTags && formData.tagIds) {
      const tags = allTags.data.filter((tag) =>
        formData.tagIds.includes(tag.id)
      )
      setSelectedTags(tags)
    }
  }, [formData.tagIds, allTags])

  const disabledStyle = { color: 'brand.lightGrayText' }

  return (
    <VStack align="stretch" spacing={10}>
      {/* Header Section */}
      <Box>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800" mb={2}>
          {t('sharedDiagnostic.steps.step3Title') || 'Résumé de la demande'}
        </Text>
        <Text fontSize="md" color="gray.600">
          {t('sharedDiagnostic.steps.step3Description') || 'Vérifiez les détails avant de soumettre votre demande de téléexpertise'}
        </Text>
      </Box>

      {/* Title Section */}
      <Box
        p={5}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <FormControl>
          <FormLabel fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
            {t('sharedDiagnostic.title') || 'Titre'}
          </FormLabel>
          <Input
            value={formData.title}
            isDisabled
            _disabled={disabledStyle}
            bg="gray.50"
            size="lg"
            fontWeight="medium"
          />
        </FormControl>
      </Box>

      {/* Description Section */}
      <Box
        p={5}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <FormControl>
          <FormLabel fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
            {t('sharedDiagnostic.description') || 'Description'}
          </FormLabel>
          <Textarea
            value={formData.description}
            isDisabled
            minH="140px"
            _disabled={disabledStyle}
            bg="gray.50"
            fontSize="md"
          />
        </FormControl>
      </Box>

      {/* Tags and Category Section */}
      <Box
        p={5}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <VStack align="stretch" spacing={5}>
          <FormControl>
            <FormLabel fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
              {t('sharedDiagnostic.tags') || 'Tags'}
            </FormLabel>
            <Box
              borderRadius="md"
              p={4}
              minH="60px"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
            >
              {selectedTags.length > 0 ? (
                <Flex gap={2} wrap="wrap">
                  {selectedTags.map((tag) => (
                    <Tag
                      key={tag.id}
                      bgColor={`${tag.color}30`}
                      fontFamily={'Poppins'}
                      letterSpacing={'0.6px'}
                      fontSize={'sm'}
                      px={3}
                      py={1}
                    >
                      #{tag.name}
                    </Tag>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="sm" color="gray.500">Aucun tag sélectionné</Text>
              )}
            </Box>
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
              {t('sharedDiagnostic.category') || 'Catégorie'}
            </FormLabel>
            <Input
              value={formData.category}
              isDisabled
              _disabled={disabledStyle}
              bg="gray.50"
              size="lg"
            />
          </FormControl>
        </VStack>
      </Box>

      {/* Uploaded Files Section */}
      <Box
        p={5}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <FormControl>
          <FormLabel fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
            {t('sharedDiagnostic.uploadedFiles') || 'Fichiers téléchargés'} ({formData.files.length})
          </FormLabel>
          {formData.files.length > 0 ? (
            <SimpleGrid
              columns={{ base: 2, sm: 3, md: 4, lg: 5 }}
              spacing={3}
              p={4}
              bg="gray.50"
              borderRadius="md"
            >
              {formData.files.map((file, index) => (
                <Box
                  key={index}
                  position="relative"
                  bg="white"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  overflow="hidden"
                  transition="all 0.2s"
                  _hover={{
                    transform: 'scale(1.05)',
                    boxShadow: 'md',
                    borderColor: 'purple.400',
                  }}
                >
                  <FilePreview file={file} width="100%" height="100px" showFileName={false} />
                  <Box
                    p={1.5}
                    bg="blackAlpha.700"
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                  >
                    <Text fontSize="xs" color="white" fontWeight="medium" noOfLines={1}>
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="whiteAlpha.800">
                      {(file.size / 1024).toFixed(1)} KB
                    </Text>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          ) : (
            <Box
              p={6}
              bg="gray.50"
              borderRadius="md"
              border="1px dashed"
              borderColor="gray.300"
              textAlign="center"
            >
              <Text fontSize="sm" color="gray.500">Aucun fichier téléchargé</Text>
            </Box>
          )}
        </FormControl>
      </Box>

      {/* Whiteboard Launch Section */}
      <Box
        as="label"
        htmlFor="openWhiteBoard"
        p={6}
        bg="green.50"
        borderRadius="lg"
        border="2px solid"
        borderColor="green.300"
        boxShadow="sm"
        cursor="pointer"
        transition="all 0.2s"
        _hover={{
          bg: 'green.100',
          borderColor: 'green.400',
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        }}
      >
        <FormControl>
          <HStack spacing={4} align="center">
            <Checkbox
              id="openWhiteBoard"
              colorScheme="green"
              size="lg"
              {...register('openWhiteBoard')}
              borderColor="green.600"
            />
            <VStack align="start" spacing={1} flex={1}>
              <HStack spacing={2}>
                <Icon as={LuRocket} color="green.700" boxSize={6} />
                <Text fontWeight="bold" fontSize="lg" color="green.900">
                  {t('sharedDiagnostic.launchWhiteboard') || 'Lancer le Whiteboard immédiatement'}
                </Text>
              </HStack>
              <Text fontSize="sm" color="green.700" pl={8}>
                {t('sharedDiagnostic.launchWhiteboardDescription') || 'Démarrer la collaboration dès la création de la demande'}
              </Text>
            </VStack>
          </HStack>
        </FormControl>
      </Box>
    </VStack>
  )
}
